<i18n>
{
  "de": {
    "audioNotPlayable": "Diese Aufnahme kann im Moment nicht auf Deinem Gerät abgespielt werden.",
    "audioConverting": "Die Aufnahme wird gerade noch vorbereitet und steht dir bald zur Verfügung."
  },
  "en": {
    "audioNotPlayable": "This audio is currently not playable on your device.",
    "audioConverting": "This audio is currently converting."
  }
}
</i18n>

<template>
  <div>
    <div
      v-if="hlsMediaAvailable || legacyMediaAvailable"
      @contextmenu="handleContextMenu"
    >
      <div
        v-show="sourcesAvailable"
        class="audio-parent"
        :class="{ 'has-meta': hasMeta }"
      >
        <div
          v-if="hasMeta"
          class="audio-meta"
        >
          <span
            v-if="audio.meta.displayName"
            class="audio-meta__title"
          >{{ audio.meta.displayName }}</span>
          <span
            v-if="audio.meta.description"
            class="audio-meta__description"
          >{{ audio.meta.description }}</span>
        </div>
        <audio
          :id="audioId"
          :ref="audioId"
          class="audio-player-box video-js video-getnext"
          :class="{ 'no-poster': noPoster }"
        >
          <p class="vjs-no-js">
            To view this video please enable JavaScript, and consider upgrading to a
            web browser that
            <a
              href="http://videojs.com/html5-video-support/"
              target="_blank"
            >supports HTML5 video</a>
          </p>
        </audio>
      </div>
      <div
        v-if="!sourcesAvailable"
        class="audio-placeholder"
      >
        {{ $t('audioNotPlayable') }}
      </div>
    </div>
    <div
      v-else
      class="audio-placeholder"
    >
      {{ $t('audioConverting') }}
    </div>
  </div>
</template>
<script>
import videojs from 'video.js'
require('@/lib/player/vjsDownload')
require('videojs-seek-buttons')

export default {
  props: ['audio', 'downloadName'],

  data() {
    return {
      hlsSource: false,
      player: null,
      seekButtons: {
        forward: 15,
        back: 15
      },
      options: {},
      waveformCanvas: null,
      waveformResizeObserver: null,
      waveformClickTarget: null,
      waveformClickHandler: null
    }
  },
  computed: {
    audioId() {
      return `audio_${this.audio.name.replaceAll('-', '')}`
    },
    hlsMediaAvailable() { return (this.audio || {}).master },
    legacyMediaAvailable() { return (this.audio || {}).path },
    sourcesAvailable() { return (this.options.sources || []).length },
    hasCoverImage() { return (this.audio || {}).meta && this.audio.meta.coverImage && !this.audio.meta.hiddenCoverImage },
    noPoster() { return !this.audio.meta.coverImage || this.audio.meta.hiddenCoverImage },
    hasMeta() {
      const m = (this.audio || {}).meta
      return !!(m && (m.displayName || m.description))
    },
    hasWaveform() {
      const m = (this.audio || {}).meta
      return !!(m && Array.isArray(m.waveform) && m.waveform.length)
    }
  },
  methods: {
    setSource() {
      if (this.hlsMediaAvailable) {
        this.hlsSource = this.audio.mimeType === 'audio/mpeg'
        if (this.hlsSource) {
          return [{
            type: 'application/x-mpegurl',
            src: this.audio.master
          }]
        }
      }
      return [{
        type: this.audio.mimeType,
        src: this.audio.path
      }]
    },
    init() {
      if (!this.$refs[this.audioId]) {
        console.log('Audio player not initialized for id', this.audioId)
        return
      }

      this.options = {
        fluid: true,
        playsinline: true,
        autoplay: false,
        controls: true,
        preload: 'auto',
        audioOnlyMode: true,
        playbackRates: [0.5, 1, 1.5, 2],
        inactivityTimeout: 0,
        controlBar: {
          volumePanel: { inline: true }
        },
        poster: this.audio.poster || '',
        sources: this.setSource(),
        language: this.$i18n.locale,
        userActions: {
          hotkeys: event => {
            switch (event.code) {
              case 'Space':
                if (this.player.paused()) {
                  this.player.play()
                } else {
                  this.player.pause()
                }
                break
              case 'ArrowRight':
                this.player.currentTime(this.player.currentTime() + this.seekButtons.forward)
                break
              case 'ArrowLeft':
                this.player.currentTime(this.player.currentTime() - this.seekButtons.back)
                break
              case 'ArrowUp':
                this.player.volume(this.player.volume() + 0.05)
                break
              case 'ArrowDown':
                this.player.volume(this.player.volume() - 0.05)
                break
              case 'KeyM':
                if (this.player.muted()) {
                  this.player.muted(false)
                } else {
                  this.player.muted(true)
                }
                break
              // case 'KeyF':
              //   if (this.player.isFullscreen()) {
              //     this.player.exitFullscreen()
              //   } else {
              //     this.player.requestFullscreen()
              //   }
              //   break
              default:
                break
            }
          }
        },
        html5: {
          vhs: {
            overrideNative: true
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false
        }
      }

      if (this.hasCoverImage) {
        const img = this.audio.meta.coverImage
        this.options.poster = `${this.audio.baseDir}/${img.fileName}.${img.fileExtension}`
        this.options.audioPosterMode = true
        this.options.bigPlayButton = true
      }

      const downloadButton = {}
      if (this.audio.meta.enableDownloadButton) {
        downloadButton.plugins = {
          vjsdownload: {
            beforeElement: 'playbackRateMenuButton',
            textControl: 'Download Audio',
            name: 'downloadButton',
            downloadName: this.addFileExtension(this.downloadName, this.audio.mimeType)
          }
        }
      }

      this.player = videojs(this.$refs[this.audioId], {
        ...this.options,
        ...downloadButton
      })

      this.player.seekButtons({
        forward: 10,
        back: 10
      })
      this.player.on('ready', () => {
        if (this.hasWaveform) this.setupWaveform()
      })
      this.player.on('ended', () => { })
      this.player.on('play', () => { })
      this.player.on('pause', () => { })
      this.player.on('error', () => { })
      this.player.on('waiting', () => { })
      this.player.on('emptied', () => { })
      this.player.on('timeupdate', () => {
        if (this.waveformCanvas) this.drawWaveform()
      })
      this.player.on('seeked', () => {
        if (this.waveformCanvas) this.drawWaveform()
      })
    },

    // Waveform canvas is inserted as a child of .vjs-progress-control after
    // video.js builds its DOM. pointer-events: none on the canvas forwards
    // clicks/drags to video.js's own slider underneath, so seeking still uses
    // the built-in handler.
    setupWaveform() {
      const root = this.$refs[this.audioId]
      if (!root || !root.parentElement) return
      // After video.js wraps the <audio>, the class moves to the wrapper div.
      const playerEl = root.closest('.audio-player-box') || root.parentElement.querySelector('.audio-player-box')
      const progressControl = playerEl && playerEl.querySelector('.vjs-progress-control')
      if (!progressControl) return

      // video.js copies audio-tag classes to the wrapper only at init time, so
      // Vue's reactive :class binding never reaches the wrapper. Toggle the
      // state class directly — it gates the CSS that hides the fallback track.
      playerEl.classList.add('has-waveform')

      const canvas = document.createElement('canvas')
      canvas.className = 'audio-waveform-canvas'
      progressControl.appendChild(canvas)
      this.waveformCanvas = canvas

      this.drawWaveform()

      if (typeof ResizeObserver !== 'undefined') {
        this.waveformResizeObserver = new ResizeObserver(() => this.drawWaveform())
        this.waveformResizeObserver.observe(progressControl)
      } else {
        window.addEventListener('resize', this.drawWaveform)
      }

      // Clicking/tapping the waveform seeks AND starts playback. video.js
      // already handles the seek via the progress-holder slider underneath,
      // but it also calls stopPropagation on `click` events — so listen on
      // the gesture-end events (`mouseup` / `touchend`) that still bubble.
      // This adds auto-play when paused; seek logic stays video.js-native.
      this.waveformClickHandler = () => {
        if (this.player && this.player.paused()) {
          const p = this.player.play()
          if (p && typeof p.catch === 'function') p.catch(() => {})
        }
      }
      this.waveformClickTarget = progressControl
      progressControl.addEventListener('mouseup', this.waveformClickHandler)
      progressControl.addEventListener('touchend', this.waveformClickHandler)
    },

    drawWaveform() {
      const canvas = this.waveformCanvas
      if (!canvas || !canvas.parentElement) return
      const peaks = this.audio && this.audio.meta && this.audio.meta.waveform
      if (!Array.isArray(peaks) || !peaks.length) return

      const rect = canvas.getBoundingClientRect()
      if (!rect.width || !rect.height) return

      const dpr = window.devicePixelRatio || 1
      if (canvas.width !== Math.round(rect.width * dpr) ||
          canvas.height !== Math.round(rect.height * dpr)) {
        canvas.width = Math.round(rect.width * dpr)
        canvas.height = Math.round(rect.height * dpr)
      }

      const ctx = canvas.getContext('2d')
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, rect.width, rect.height)

      const cs = getComputedStyle(canvas)
      const accent = cs.getPropertyValue('--ap-accent').trim() || '#fb3e52'
      const ink = cs.getPropertyValue('--ap-ink').trim() || '#333333'

      const n = peaks.length
      const slot = rect.width / n
      const barWidth = Math.max(1, slot * 0.55)
      const mid = rect.height / 2

      const duration = (this.player && this.player.duration && this.player.duration()) || 0
      const current = (this.player && this.player.currentTime && this.player.currentTime()) || 0
      const playedX = duration > 0 ? (current / duration) * rect.width : 0

      for (let i = 0; i < n; i++) {
        const x = i * slot + (slot - barWidth) / 2
        const h = Math.max(2, peaks[i] * rect.height)
        const y = mid - h / 2
        const barCenter = x + barWidth / 2
        ctx.fillStyle = barCenter <= playedX ? accent : this.hexToRgba(ink, 0.28)
        ctx.fillRect(x, y, barWidth, h)
      }
    },

    hexToRgba(hex, alpha) {
      let h = hex.replace('#', '')
      if (h.length === 3) h = h.split('').map(c => c + c).join('')
      if (h.length !== 6) return `rgba(128,128,128,${alpha})`
      const r = parseInt(h.slice(0, 2), 16)
      const g = parseInt(h.slice(2, 4), 16)
      const b = parseInt(h.slice(4, 6), 16)
      return `rgba(${r},${g},${b},${alpha})`
    },

    teardownWaveform() {
      if (this.waveformResizeObserver) {
        this.waveformResizeObserver.disconnect()
        this.waveformResizeObserver = null
      } else {
        window.removeEventListener('resize', this.drawWaveform)
      }
      if (this.waveformClickTarget && this.waveformClickHandler) {
        this.waveformClickTarget.removeEventListener('mouseup', this.waveformClickHandler)
        this.waveformClickTarget.removeEventListener('touchend', this.waveformClickHandler)
      }
      this.waveformClickTarget = null
      this.waveformClickHandler = null
      if (this.waveformCanvas && this.waveformCanvas.parentElement) {
        const playerEl = this.waveformCanvas.closest('.audio-player-box')
        if (playerEl) playerEl.classList.remove('has-waveform')
        this.waveformCanvas.parentElement.removeChild(this.waveformCanvas)
      }
      this.waveformCanvas = null
    },

    addFileExtension(name, mimeType) {
      switch (mimeType) {
        case 'audio/mpeg':
          return `${name}.mp3`
        case 'audio/wav':
          return `${name}.wav`
        case 'audio/ogg':
          return `${name}.oga`
        default:
          return `${name}.mp3`
      }
    },
    handleContextMenu(event) {
      event.preventDefault()
    }
  },

  mounted() {
    // setTimeout(() => {
    //   this.init()
    // }, 200)
    this.$nextTick(() => { this.init() })
  },
  beforeDestroy() {
    this.teardownWaveform()
    if (this.player) this.player.dispose()
  }
}
</script>

<style lang="scss">
@import 'assets/sass/video-player';

.audio-parent {
  position: relative;

  &.has-meta {
    // Shared color tokens (kept in sync with .audio-player-box fallbacks)
    --ap-tile: var(--posts-background-color, var(--tiles-background-color, #fdfdfd));
    --ap-ink: var(--posts-background-text-color, var(--tiles-background-text-color, #333));
    --ap-accent: var(--primary-color, #fb3e52);

    .audio-meta {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 10px 18px 8px;
      font-size: 13px;
      line-height: 1.3;
      color: var(--ap-ink);
      background: linear-gradient(
        135deg,
        color-mix(in hsl, var(--ap-tile), var(--ap-accent) 6%) 0%,
        color-mix(in hsl, var(--ap-tile), var(--ap-accent) 14%) 100%
      );
      border-radius: 14px 14px 0 0;
      border-bottom: 1px solid color-mix(in hsl, var(--ap-accent), transparent 85%);

      &__title {
        font-weight: 600;
      }

      &__description {
        opacity: 0.75;
      }
    }

    // Player loses its top rounding — card feels like one piece
    .audio-player-box.no-poster {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }
}

.audio-placeholder {
  width: 100%;
  padding: 14px 18px;
  font-style: italic;
  border-radius: 12px;
  background-color: color-mix(in hsl, var(--tiles-background-color, #f0f0f0), var(--tiles-background-text-color, #333) 8%);
  color: color-mix(in hsl, var(--tiles-background-text-color, #333), transparent 30%);
  border: 1px solid color-mix(in hsl, var(--tiles-background-color, #f0f0f0), var(--tiles-background-text-color, #333) 15%);
}

.audio-player-box {
  .vjs-playback-rate {
    width: 5em !important;

    .vjs-menu {
      left: -3em;
    }
  }

  .vjs-picture-in-picture-control {
    display: none;
  }

  // videojs-vjsdownload builds its icon from ::before (rotated arrow) + ::after
  // (white tray) using offsets tuned for display:block. Our flex-centered
  // control bar shifts the pair off-center; re-anchor relative to the button
  // and swap the hardcoded white tray for currentColor so theming applies.
  .vjs-button.vjs-vjsdownload {
    &::before {
      position: absolute !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, calc(-50% - 3px)) rotate(90deg) !important;
      font-size: 1.2em !important;
      line-height: 1 !important;
    }
    &::after {
      position: absolute !important;
      top: calc(50% + 8px) !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      width: 14px !important;
      height: 2px !important;
      border-radius: 1px !important;
      background-color: currentColor !important;
    }
  }

  // Fallback tokens when component renders outside #dynamic-color-scope
  --ap-tile: var(--posts-background-color, var(--tiles-background-color, #fdfdfd));
  --ap-ink: var(--posts-background-text-color, var(--tiles-background-text-color, #333));
  --ap-accent: var(--primary-color, #fb3e52);

  &.no-poster {
    padding-top: 0px;
    height: 92px;
    border-radius: 14px;
    overflow: hidden;
    background:
      linear-gradient(
        135deg,
        color-mix(in hsl, var(--ap-tile), var(--ap-accent) 8%) 0%,
        color-mix(in hsl, var(--ap-tile), var(--ap-accent) 22%) 100%
      );
    box-shadow:
      0 0 0 1px color-mix(in hsl, var(--ap-accent), transparent 85%),
      0 2px 14px color-mix(in hsl, var(--ap-accent), transparent 88%);
    transition: box-shadow 0.3s ease;

    &:hover {
      box-shadow:
        0 0 0 1px color-mix(in hsl, var(--ap-accent), transparent 70%),
        0 4px 22px color-mix(in hsl, var(--ap-accent), transparent 75%);
    }

    &.vjs-playing {
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: linear-gradient(
          120deg,
          transparent 0%,
          color-mix(in hsl, var(--ap-accent), transparent 85%) 50%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: audio-shimmer 6s linear infinite;
        border-radius: inherit;
        z-index: 0;
      }
    }

    .vjs-big-play-button {
      display: none !important;
    }

    &:not(.vjs-has-started) {
      .vjs-control-bar {
        display: flex;
      }
    }

    .vjs-control-bar {
      position: absolute !important;
      left: 8px !important;
      right: 8px !important;
      top: auto !important;
      bottom: 8px !important;
      transform: none;
      width: auto !important;
      height: 48px !important;
      padding: 0 10px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      gap: 2px;
      background: color-mix(in hsl, var(--ap-tile), transparent 35%) !important;
      backdrop-filter: blur(16px) saturate(140%);
      -webkit-backdrop-filter: blur(16px) saturate(140%);
      border: 1px solid color-mix(in hsl, var(--ap-ink), transparent 85%);
      color: var(--ap-ink) !important;
      z-index: 1;

      .vjs-playback-rate {
        .vjs-menu {
          display: none;
        }
      }

      // Each control renders its icon/text flex-centered so buttons, time
      // displays and the rate badge share a common baseline in the 36px row.
      // Progress bar excluded (absolute-positioned strip at top).
      // `:not(.vjs-hidden)` preserves video.js's hide mechanism.
      .vjs-control:not(.vjs-progress-control):not(.vjs-hidden),
      .vjs-time-control:not(.vjs-hidden) {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        height: 36px !important;
        line-height: 1 !important;
      }

      // Icon glyphs centered inside their buttons
      .vjs-button .vjs-icon-placeholder {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: auto;
        height: 100%;
        line-height: 1;

        &::before {
          position: static;
          font-size: 1.6em;
          line-height: 1;
          width: auto;
          height: auto;
        }
      }

      // Playback rate: video.js absolute-positions the "1x" label at top:0
      // of its parent. Re-center it vertically so it aligns with sibling controls.
      .vjs-playback-rate {
        position: relative;

        .vjs-playback-rate-value {
          position: absolute;
          top: 50% !important;
          left: 0;
          right: 0;
          transform: translateY(-50%);
          line-height: 1 !important;
          height: auto;
          text-align: center;
          pointer-events: none;
        }
      }

      // Spacer fills remaining row without stretching vertically
      .vjs-custom-control-spacer {
        flex: 1 1 auto;
        height: auto !important;
      }

      // Hide unused controls. The `:not(.vjs-progress-control)` pseudo lifts
      // the leaf selector to 3 classes, matching the flex rule's specificity
      // above — source order then picks this rule for the tiebreak.
      .vjs-fullscreen-control.vjs-control:not(.vjs-progress-control),
      .vjs-seek-to-live-control.vjs-control:not(.vjs-progress-control),
      .vjs-remaining-time.vjs-time-control:not(.vjs-progress-control),
      .vjs-live-control.vjs-control:not(.vjs-progress-control),
      .vjs-picture-in-picture-control.vjs-control:not(.vjs-progress-control),
      .vjs-chapters-button.vjs-control:not(.vjs-progress-control),
      .vjs-descriptions-button.vjs-control:not(.vjs-progress-control),
      .vjs-subs-caps-button.vjs-control:not(.vjs-progress-control),
      .vjs-audio-button.vjs-control:not(.vjs-progress-control) {
        display: none !important;
      }
    }

    .vjs-play-control {
      position: relative;
      transition: transform 0.15s ease;

      &:hover {
        transform: scale(1.08);
      }
    }

    &.vjs-playing .vjs-play-control::after {
      content: '';
      position: absolute;
      inset: 10%;
      border-radius: 50%;
      box-shadow: 0 0 0 0 color-mix(in hsl, var(--ap-accent), transparent 50%);
      animation: audio-play-pulse 1.8s ease-out infinite;
      pointer-events: none;
    }

    // Progress bar pinned to player top. Thin visible track centered inside
    // a tall (14px desktop / 22px touch) hit area so mobile users can scrub
    // reliably. Control-bar is bottom-pinned so hit area never overlaps buttons.
    .vjs-progress-control {
      position: absolute !important;
      left: 0 !important;
      right: 0 !important;
      top: auto !important;
      // Stacked above control-bar (positioned parent) with 6px clearance
      bottom: calc(100% + 6px) !important;
      width: auto !important;
      height: 28px !important;
      display: flex;
      align-items: center;
      cursor: pointer;

      .vjs-progress-holder {
        position: relative;
        margin: 0;
        width: 100%;
        height: 100%;
        background-color: transparent;
        border-radius: 0;
        overflow: visible;

        // Visible track line, centered inside the hit area
        &::before {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          height: 3px;
          background: color-mix(in hsl, var(--ap-ink), transparent 78%);
          border-radius: 999px;
          transition: height 0.15s ease;
          pointer-events: none;
        }
      }

      .vjs-play-progress {
        position: absolute !important;
        top: 50% !important;
        transform: translateY(-50%);
        height: 3px !important;
        border-radius: 999px !important;
        box-shadow: 0 0 8px color-mix(in hsl, var(--ap-accent), transparent 40%);
        transition: height 0.15s ease;
      }

      .vjs-load-progress {
        position: absolute !important;
        top: 50% !important;
        transform: translateY(-50%);
        height: 3px !important;
        background-color: color-mix(in hsl, var(--ap-ink), transparent 85%);
        border-radius: 999px !important;
        transition: height 0.15s ease;
      }

      // Hide default time tooltip bubble + mouse display (both flash briefly
      // above the bar on hover otherwise)
      .vjs-mouse-display,
      .vjs-time-tooltip {
        display: none !important;
      }

      &:hover,
      &.vjs-slider-active {
        .vjs-progress-holder::before,
        .vjs-play-progress,
        .vjs-load-progress {
          height: 6px !important;
        }
      }

      // Waveform canvas overlay — injected after video.js DOM is built.
      // pointer-events: none keeps the underlying progress-holder clickable
      // so seek still uses video.js's own slider logic.
      .audio-waveform-canvas {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 2;
      }
    }

    // The volume popup hovers up into the progress-control area; bump it
    // above the waveform canvas (z-index: 2) so it renders in front.
    // z-index only applies to positioned elements — add position:relative
    // to the panel so the declaration takes effect.
    .vjs-volume-panel {
      position: relative !important;
      z-index: 3 !important;
    }

    .vjs-volume-control,
    .vjs-volume-tooltip,
    .vjs-volume-panel .vjs-menu {
      z-index: 3 !important;
    }

    // When waveform data is rendered, hide the default thin-line track +
    // play/load progress fills — the canvas replaces them visually.
    &.has-waveform .vjs-progress-control {
      .vjs-progress-holder::before,
      .vjs-play-progress,
      .vjs-load-progress {
        display: none !important;
      }
    }

    // Touch devices: larger hit area + thicker visible track
    @media (pointer: coarse) {
      height: 108px;

      .vjs-progress-control {
        height: 44px !important;

        .vjs-progress-holder::before,
        .vjs-play-progress,
        .vjs-load-progress {
          height: 5px !important;
        }

        &.vjs-slider-active {
          .vjs-progress-holder::before,
          .vjs-play-progress,
          .vjs-load-progress {
            height: 8px !important;
          }
        }
      }
    }

    .vjs-fullscreen-control {
      display: none;
    }
  }

  &.hidden-video {

    .vjs-captions-button,
    .vjs-chapters-button,
    .vjs-descriptions-button,
    .vjs-tech {
      display: none;
    }
  }

  &.hidden-controls {

    .vjs-current-time,
    .vjs-time-divider,
    .vjs-time-control,
    .vjs-playback-rate {
      display: none
    }
  }

}

@keyframes audio-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@keyframes audio-play-pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in hsl, var(--primary-text-color, #fff), transparent 50%);
  }
  70% {
    box-shadow: 0 0 0 10px color-mix(in hsl, var(--primary-text-color, #fff), transparent 100%);
  }
  100% {
    box-shadow: 0 0 0 0 color-mix(in hsl, var(--primary-text-color, #fff), transparent 100%);
  }
}
</style>
